'use client'

import { format } from 'date-fns'
import React, { useState } from 'react'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/_components/ui/tooltip'
import { cn } from '@/_lib/utils'

import { useCalendarCore } from './a-product-template-calendar-core'
import { AProductTemplateCalendarLoading } from './a-product-template-calendar-loading'
import { AProductTemplateCalendarWeekNav } from './a-product-template-calendar-week-nav'

import type { CalendarTimeslot, DateSlot } from './a-product-template-calendar-types'

type WeekDay = {
	date: Date
	dayName: string
	dayShort: string
	dayNumber: string
	fullDate: string
	isToday: boolean
}

/**
 * Fixed width for the day label column; must match `gridTemplateColumns` in the header and rows.
 */
const DESKTOP_CAL_DAY_COL = '5.25rem'

/**
 * Desktop week grid: hour timeline header and one row per weekday with positioned slot blocks.
 */
interface AProductTemplateCalendarDesktopProps {
	slots?: DateSlot[]
	isSlotsLoading?: boolean
	onSlotSelect?: (selectedSlot: CalendarTimeslot) => void
	startHour?: number
	endHour?: number
	dateFrom?: string
	dateTo?: string
	onWeekChange?: (dateFrom: string, dateTo: string) => void
}

/**
 * Thin wrapper that forwards week navigation into {@link AProductTemplateCalendarWeekNav}.
 */
function AProductTemplateCalendarDesktopControls({
	weekRange,
	onPrevWeek,
	onNextWeek,
	className
}: {
	weekRange: string
	onPrevWeek: () => void
	onNextWeek: () => void
	className?: string
}) {
	return (
		<AProductTemplateCalendarWeekNav
			className={className}
			weekRange={weekRange}
			onPrevWeek={onPrevWeek}
			onNextWeek={onNextWeek}
		/>
	)
}

/**
 * Splits an `HH:mm` hour label into a large hour digit and small minutes segment for the header.
 *
 * @param hour - Label like `07:00` or `9:30`.
 */
function parseHourColumnLabel(hour: string): { hourBig: string; minutesSmall: string } {
	const [h, m = '00'] = hour.split(':')
	return {
		hourBig: String(parseInt(h, 10)),
		minutesSmall: m.padStart(2, '0')
	}
}

/**
 * Decorative vertical hour dividers behind the timeline area.
 *
 * @param colCount - Number of hour columns (typically `endHour - startHour`).
 */
function AProductTemplateCalendarDesktopTimelineGrid({
	colCount,
	className
}: {
	colCount: number
	className?: string
}) {
	if (colCount <= 0) return null
	return (
		<div
			aria-hidden
			className={cn('pointer-events-none absolute inset-0 grid', className)}
			style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}
		>
			{Array.from({ length: colCount }, (_, i) => (
				<div key={i} className={cn(i > 0 && 'border-l border-medante-purple-dark/10')} />
			))}
		</div>
	)
}

/**
 * Top grid row: “Deň” label plus evenly spaced hour labels across the timeline width.
 *
 * @param hours - Strings like `07:00` produced by {@link useCalendarCore}'s `hours` array.
 */
function AProductTemplateCalendarDesktopHeader({ hours }: { hours: string[] }) {
	const colCount = hours.length
	const segmentPct = colCount > 0 ? 100 / colCount : 0

	return (
		<div
			className='grid items-stretch border-b border-medante-purple-dark/15'
			style={{
				gridTemplateColumns: `${DESKTOP_CAL_DAY_COL} minmax(0, 1fr)`
			}}
		>
			<div className='flex items-center border-r border-medante-purple-dark/15 px-2 py-2 text-sm font-semibold text-medante-purple-dark'>
				Deň
			</div>
			<div className='relative min-h-11 min-w-0 py-2'>
				<AProductTemplateCalendarDesktopTimelineGrid
					colCount={colCount}
					className='inset-x-0 top-2 bottom-2'
				/>
				{hours.map((hour, index) => {
					const { hourBig, minutesSmall } = parseHourColumnLabel(hour)
					return (
						<div
							key={index}
							className='pointer-events-none absolute inset-y-0 flex items-center justify-center py-1 text-center'
							style={{
								left: `${index * segmentPct}%`,
								width: `${segmentPct}%`
							}}
						>
							<span className='inline-flex items-start gap-px leading-none'>
								<span className='-mt-0.5 text-base font-medium tracking-tight text-medante-purple-dark'>
									{hourBig}
								</span>
								<span className='mt-px text-xs font-normal leading-none text-medante-purple-dark/55'>
									{minutesSmall}
								</span>
							</span>
						</div>
					)
				})}
			</div>
		</div>
	)
}

/**
 * Absolutely positioned slot pill inside a day row, with tooltip showing full interval text.
 */
function AProductTemplateCalendarDesktopTimeslot({
	day,
	slotStartTime,
	startPercentage,
	widthPercentage,
	endTime,
	isSelected,
	onSelect
}: {
	day: WeekDay
	slotStartTime: string
	startPercentage: number
	widthPercentage: number
	endTime: string
	isSelected: boolean
	onSelect: () => void
}) {
	const tooltipLabel = `${day.dayName} ${slotStartTime} – ${endTime}`
	const endPercentage = Math.min(100, startPercentage + widthPercentage)
	const rightPercentage = Math.max(0, 100 - endPercentage)

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type='button'
					className={cn(
						'absolute z-10 block h-9 min-h-0 min-w-0 cursor-pointer border-none bg-transparent p-0 transition-all duration-200',
						isSelected ? 'z-20' : 'hover:z-20'
					)}
					style={{
						left: `${startPercentage}%`,
						right: `${rightPercentage}%`,
						top: '50%',
						transform: 'translateY(-50%)',
						width: 'auto'
					}}
					onClick={onSelect}
				>
					<div
						className={cn(
							'absolute inset-px flex min-h-0 min-w-0 items-center justify-center rounded-lg',
							'bg-medante-pink-secondary shadow-md transition-all duration-300',
							isSelected
								? 'scale-105 bg-pink-800 shadow-lg'
								: 'hover:bg-medante-purple-dark hover:shadow-lg'
						)}
					>
						<span className='text-base font-bold leading-none text-white'>+</span>
					</div>
				</button>
			</TooltipTrigger>
			<TooltipContent side='top' sideOffset={8}>
				{tooltipLabel}
			</TooltipContent>
		</Tooltip>
	)
}

/**
 * One weekday row: label cell + timeline containing all slots for `day.fullDate`.
 */
function AProductTemplateCalendarDesktopDayRow({
	day,
	daySlots,
	colCount,
	startHour,
	endHour,
	timeToPercentage,
	getEndTime,
	convertSlotToTimeslot,
	selectedStartTime,
	onSlotClick
}: {
	day: WeekDay
	daySlots: DateSlot[]
	colCount: number
	startHour: number
	endHour: number
	timeToPercentage: (time: string) => number
	getEndTime: (startTime: string, duration: number) => string
	convertSlotToTimeslot: (slot: DateSlot) => CalendarTimeslot
	selectedStartTime: string | null
	onSlotClick: (day: WeekDay, slot: DateSlot) => void
}) {
	const dayDurationMinutes = (endHour - startHour) * 60

	return (
		<div
			className='grid items-stretch border-b border-medante-purple-dark/10 transition-colors duration-200 hover:bg-medante-purple-dark/[0.04]'
			style={{
				gridTemplateColumns: `${DESKTOP_CAL_DAY_COL} minmax(0, 1fr)`
			}}
		>
			<div className='flex flex-col justify-center border-r border-medante-purple-dark/15 px-2 py-2 font-medium'>
				<div className='mb-1 text-xs capitalize text-medante-purple-dark'>{day.dayName}</div>
				<div className='text-xs text-medante-purple-dark/55'>{format(day.date, 'd.M.yyyy')}</div>
			</div>
			<div className='relative min-h-12 min-w-0'>
				<AProductTemplateCalendarDesktopTimelineGrid colCount={colCount} />
				{daySlots.map((slot, slotIndex) => {
					const startPercentage = timeToPercentage(slot.startTime)
					const widthPercentage = (slot.duration / dayDurationMinutes) * 100
					const endTime = getEndTime(slot.startTime, slot.duration)
					const timeslot = convertSlotToTimeslot(slot)
					const isSelected = selectedStartTime === timeslot.startTime

					return (
						<AProductTemplateCalendarDesktopTimeslot
							key={slotIndex}
							day={day}
							slotStartTime={slot.startTime}
							startPercentage={startPercentage}
							widthPercentage={widthPercentage}
							endTime={endTime}
							isSelected={isSelected}
							onSelect={() => onSlotClick(day, slot)}
						/>
					)
				})}
			</div>
		</div>
	)
}

/** @see {@link AProductTemplateCalendarDesktopProps} */
const AProductTemplateCalendarDesktop: React.FC<AProductTemplateCalendarDesktopProps> = ({
	slots = [],
	isSlotsLoading = false,
	onSlotSelect,
	startHour = 7,
	endHour = 20,
	dateFrom,
	dateTo,
	onWeekChange
}) => {
	const {
		weekDays,
		hours,
		getDaySlots,
		convertSlotToTimeslot,
		timeToPercentage,
		getEndTime,
		goToPrevWeek,
		goToNextWeek,
		weekRange
	} = useCalendarCore(slots, startHour, endHour, dateFrom, dateTo, onWeekChange)

	const [selectedSlot, setSelectedSlot] = useState<CalendarTimeslot | null>(null)

	const handleSlotClick = (day: WeekDay, slot: DateSlot) => {
		const timeslot = convertSlotToTimeslot(slot)
		setSelectedSlot(timeslot)
		onSlotSelect?.(timeslot)
	}

	const colCount = endHour - startHour
	const selectedStartTime = selectedSlot?.startTime ?? null

	return (
		<TooltipProvider delayDuration={200}>
			<div className='w-full overflow-hidden p-5'>
				<AProductTemplateCalendarDesktopControls
					className='mb-5'
					weekRange={weekRange}
					onPrevWeek={goToPrevWeek}
					onNextWeek={goToNextWeek}
				/>
				<AProductTemplateCalendarDesktopHeader hours={hours} />
				{isSlotsLoading ? (
					<AProductTemplateCalendarLoading variant='desktop' />
				) : (
					weekDays.map((day, dayIndex) => (
						<AProductTemplateCalendarDesktopDayRow
							key={dayIndex}
							day={day}
							daySlots={getDaySlots(day.fullDate)}
							colCount={colCount}
							startHour={startHour}
							endHour={endHour}
							timeToPercentage={timeToPercentage}
							getEndTime={getEndTime}
							convertSlotToTimeslot={convertSlotToTimeslot}
							selectedStartTime={selectedStartTime}
							onSlotClick={handleSlotClick}
						/>
					))
				)}
			</div>
		</TooltipProvider>
	)
}

export default AProductTemplateCalendarDesktop
